import uuidService from './uuidService'

/**
 * Client-side JWT Token Service
 *
 * Manages anonymous user sessions with client-generated JWT tokens.
 * Provides conversation persistence, analytics tracking, and seamless
 * upgrade to authenticated accounts.
 */

class ClientTokenService {
  constructor() {
    this.storageKeys = {
      anonymousId: 'et_anonymous_id',
      conversationsUUID: 'et_conversations_uuid',
      sessionToken: 'et_session_token',
      sessionStartTime: 'et_session_start_time',
    }

    this.tokenExpiration = 30 * 60 * 1000 // 30 minutes in milliseconds
    this.maxHistorySize = 50 // Maximum number of messages to store

    // Initialize on construction
    this.initialize()
  }

  /**
   * Initialize the service with anonymous ID
   */
  initialize() {
    const anonymousId = this.getOrCreateAnonymousId()
    const sessionStartTime = this.getOrCreateSessionStartTime()

    return {
      anonymousId,
      sessionStartTime,
      token: this.getCurrentToken(),
    }
  }

  /**
   * Generate or retrieve persistent anonymous ID for analytics tracking
   * @returns {string} Anonymous ID in format 'anon_uuid'
   */
  getOrCreateAnonymousId() {
    let anonymousId = localStorage.getItem(this.storageKeys.anonymousId)

    if (!anonymousId) {
      // Generate cryptographically secure anonymous ID
      const randomBytes = new Uint8Array(16)
      crypto.getRandomValues(randomBytes)

      // Convert to hex string
      const hexString = Array.from(randomBytes)
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('')

      // Format as UUID-like string with 'anon_' prefix
      const uuid = [
        hexString.slice(0, 8),
        hexString.slice(8, 12),
        hexString.slice(12, 16),
        hexString.slice(16, 20),
        hexString.slice(20, 32),
      ].join('-')

      anonymousId = `anon_${uuid}`
      localStorage.setItem(this.storageKeys.anonymousId, anonymousId)
    }

    return anonymousId
  }

  /**
   * Get or create session start time for analytics
   * @returns {number} Timestamp when session started
   */
  getOrCreateSessionStartTime() {
    let sessionStartTime = sessionStorage.getItem(this.storageKeys.sessionStartTime)

    if (!sessionStartTime) {
      sessionStartTime = Date.now().toString()
      sessionStorage.setItem(this.storageKeys.sessionStartTime, sessionStartTime)
    }

    return parseInt(sessionStartTime, 10)
  }

  /**
   * Generate a new session ID
   * @returns {string} Cryptographically secure session ID
   */
  generateSessionId() {
    const randomBytes = new Uint8Array(16)
    crypto.getRandomValues(randomBytes)

    return Array.from(randomBytes)
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('')
  }

  /**
   * Generate client-side JWT token
   * @param {string} userType - 'anonymous' or 'authenticated'
   * @param {string|null} userId - User ID for authenticated users, null for anonymous
   * @param {Object} metadata - Additional metadata to include
   * @returns {string} JWT token string
   */
  generateToken(userType = 'anonymous', userId = null, metadata = {}) {
    const now = Date.now()
    const anonymousId = this.getOrCreateAnonymousId()
    const sessionId = this.generateSessionId()

    const header = {
      alg: 'none',
      typ: 'JWT',
    }

    const payload = {
      sessionId,
      anonymousId,
      userId,
      level: userType === 'authenticated' ? metadata.level || 'guest' : 'guest',
      iat: Math.floor(now / 1000),
      exp: Math.floor((now + this.tokenExpiration) / 1000),
      ...metadata,
    }

    // Create JWT with empty signature (client-side token)
    const encodedHeader = btoa(JSON.stringify(header))
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_')

    const encodedPayload = btoa(JSON.stringify(payload))
      .replace(/=/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_')

    const token = `${encodedHeader}.${encodedPayload}.`

    // Store token for future use
    sessionStorage.setItem(this.storageKeys.sessionToken, token)

    return token
  }

  /**
   * Get current valid token or generate new one
   * @returns {string} Valid JWT token
   */
  getCurrentToken() {
    const existingToken = sessionStorage.getItem(this.storageKeys.sessionToken)

    if (existingToken && this.isTokenValid(existingToken)) {
      return existingToken
    }

    // Generate new token if none exists or expired
    return this.generateToken()
  }

  /**
   * Parse JWT token payload
   * @param {string} token - JWT token string
   * @returns {Object|null} Parsed payload or null if invalid
   */
  parseToken(token) {
    try {
      const parts = token.split('.')
      if (parts.length !== 3) return null

      const payload = JSON.parse(atob(parts[1]))
      return payload
    } catch (error) {
      console.warn('Failed to parse token:', error)
      return null
    }
  }

  /**
   * Check if token is valid and not expired
   * @param {string} token - JWT token string
   * @returns {boolean} True if token is valid
   */
  isTokenValid(token) {
    const payload = this.parseToken(token)
    if (!payload || !payload.exp) return false

    const now = Math.floor(Date.now() / 1000)
    return payload.exp > now
  }

  /**
   * Upgrade anonymous session to authenticated user
   * @param {Object} supabaseUser - Supabase user object
   * @returns {Object} Migration result with analytics data
   */
  upgradeToAuthenticated(supabaseUser) {
    const anonymousId = this.getAnonymousId()
    const conversationHistory = this.getConversationHistory()
    const sessionStartTime = this.getOrCreateSessionStartTime()

    // Create migration tracking data
    const migrationData = {
      anonymousId,
      userId: supabaseUser.id,
      email: supabaseUser.email,
      migrationTime: Date.now(),
      sessionStartTime,
      conversationCount: conversationHistory.length,
      provider: supabaseUser.app_metadata?.provider || 'email',
    }

    // Generate new authenticated token (will be replaced by Supabase JWT)
    const authenticatedToken = this.generateToken('authenticated', supabaseUser.id, {
      level: supabaseUser.app_metadata?.user_level || 'guest',
      email: supabaseUser.email,
    })

    return {
      migrationData,
      authenticatedToken,
      preservedHistory: conversationHistory,
    }
  }

  /**
   * Get current anonymous ID
   * @returns {string} Current anonymous ID
   */
  getAnonymousId() {
    return this.getOrCreateAnonymousId()
  }

  /**
   * Get conversation history for current session from UUID-based storage
   * @returns {Array} Array of conversation messages from most recent conversation
   */
  getConversationHistory() {
    try {
      const conversations = this.getConversationsWithUUID()
      if (!conversations || Object.keys(conversations).length === 0) return []

      // Get the most recently updated conversation
      const sortedConversations = Object.values(conversations).sort(
        (a, b) => (b.updated_at || 0) - (a.updated_at || 0)
      )

      if (sortedConversations.length > 0 && sortedConversations[0].messages) {
        const conversation = sortedConversations[0]

        // Convert messages object back to array using message_order
        if (conversation.message_order && Array.isArray(conversation.message_order)) {
          const restoredMessages = conversation.message_order
            .map((messageId) => conversation.messages[messageId])
            .filter((message) => message) // Filter out any missing messages
            .map((message) => this.restoreMessageStructure(message)) // Enhanced restoration

          return restoredMessages
        }

        // Fallback for old format (should not happen in new app)
        if (Array.isArray(conversation.messages)) {
          return conversation.messages.map((message) => this.restoreMessageStructure(message))
        }
      }

      return []
    } catch (error) {
      console.warn('Failed to load conversation history:', error)
      return []
    }
  }

  /**
   * Restores message structure from storage, ensuring proper parts structure and backward compatibility.
   * @param {Object} storedMessage - Message object from localStorage
   * @returns {Object} Restored message with proper structure
   */
  restoreMessageStructure(storedMessage) {
    if (!storedMessage || typeof storedMessage !== 'object') {
      console.warn('Invalid stored message:', storedMessage)
      return null
    }

    // Create base restored message
    const restoredMessage = {
      // Core message fields
      role: storedMessage.role || 'assistant',
      timestamp: storedMessage.timestamp || new Date().toISOString(),

      // UUID fields (prefer underscore format from storage, fallback to camelCase)
      messageId: storedMessage.message_id || storedMessage.messageId,
      conversationId: storedMessage.conversation_id || storedMessage.conversationId,

      // Legacy compatibility fields
      message_id: storedMessage.message_id || storedMessage.messageId,
      conversation_id: storedMessage.conversation_id || storedMessage.conversationId,

      // State flags (never stored as true, always false when restored)
      isProgress: false,
      isStreaming: false,
      currentStatus: null,
      statusType: null,

      // Metadata preservation
      lastUpdated: storedMessage.lastUpdated || storedMessage.timestamp || new Date().toISOString(),
      streamingDuration: storedMessage.streamingDuration || 0,
      isError: Boolean(storedMessage.isError || storedMessage.type === 'error'),
      type: storedMessage.type || 'response',
    }

    // Handle parts-based messages (new format)
    if (storedMessage.parts && Array.isArray(storedMessage.parts)) {
      // Restore and validate parts structure
      restoredMessage.parts = this.restorePartsStructure(storedMessage.parts)

      // Reconstruct content from parts for display compatibility (not stored)
      restoredMessage.content = this.constructContentFromParts(restoredMessage.parts)
    }
    // Handle legacy content-based messages (backward compatibility)
    else if (storedMessage.content) {
      restoredMessage.content = storedMessage.content
      restoredMessage.parts = [] // Empty parts array for consistency
    }
    // Handle legacy message format
    else if (storedMessage.message) {
      restoredMessage.content = storedMessage.message
      restoredMessage.parts = [] // Empty parts array for consistency
    }

    return restoredMessage
  }

  /**
   * Restores parts structure from storage, ensuring proper order and component preservation.
   * @param {Array} storedParts - Parts array from localStorage
   * @returns {Array} Restored and validated parts array
   */
  restorePartsStructure(storedParts) {
    if (!Array.isArray(storedParts)) {
      console.warn('Stored parts is not an array:', storedParts)
      return []
    }

    return storedParts
      .filter((part) => {
        // Filter out invalid parts
        if (!part || typeof part !== 'object') {
          console.warn('Invalid part found during restoration:', part)
          return false
        }

        // Ensure part has required content
        if (!part.message && !part.content) {
          console.warn('Part missing content during restoration:', part)
          return false
        }

        return true
      })
      .map((part) => {
        // Restore and validate each part
        const restoredPart = {
          // Core fields
          type: part.type || 'response',
          message: part.message || part.content || '',
          timestamp: part.timestamp || new Date().toISOString(),
          order: typeof part.order === 'number' ? part.order : 0,

          // Component fields
          isComponent: Boolean(part.isComponent),
          componentType: part.componentType || null,

          // UUID fields (map storage format to component format)
          sentence_id: part.sentence_id || null,
          message_id: part.message_id || null,
          conversation_id: part.conversation_id || null,

          // Metadata
          metadata: part.metadata || {},
        }

        // Restore component data if present
        if (part.componentData) {
          restoredPart.componentData = part.componentData
        }

        // Restore component-specific fields
        const componentFields = [
          'title',
          'level',
          'text',
          'action',
          'url',
          'variant',
          'code',
          'language',
          'params',
          'displayText',
          'originalMarker',
        ]
        componentFields.forEach((field) => {
          if (part[field] !== undefined) {
            restoredPart[field] = part[field]
          }
        })

        // Restore clickable sentence data
        if (part.sentences && Array.isArray(part.sentences)) {
          restoredPart.sentences = part.sentences
        }

        // Restore additional preserved fields
        const preservedFields = ['components', 'isTLDR', 'referenceId', 'event_id', 'source']
        preservedFields.forEach((field) => {
          if (part[field] !== undefined) {
            restoredPart[field] = part[field]
          }
        })

        return restoredPart
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0)) // Ensure proper ordering
  }

  /**
   * Constructs display content from parts array for backward compatibility.
   * Mirrors the logic in useChatState.js constructContentFromParts function.
   * @param {Array} parts - Array of message parts
   * @returns {string} Concatenated text content for display
   */
  constructContentFromParts(parts) {
    if (!parts || parts.length === 0) return ''

    return parts
      .filter(
        (part) =>
          // Include parts that should be visible (including sentence parts)
          part.type === 'response' ||
          part.type === 'error' ||
          part.isComponent ||
          part.sentenceUUID ||
          part.sentence_id // Support both field names
      )
      .map((part) => {
        // If the part has clickable sentences, extract text from sentences array
        if (part.sentences && Array.isArray(part.sentences) && part.sentences.length > 0) {
          return part.sentences.map((sentence) => sentence.text).join(' ')
        }
        // Otherwise use the message field
        return part.message || ''
      })
      .join('')
  }

  /**
   * Save conversation history with a generated conversation ID
   * This method generates a proper UUID v5 conversation ID using the UUID service
   * @param {Array} messages - Array of conversation messages
   * @throws {Error} If UUID service is not properly configured
   */
  saveConversationHistory(messages) {
    // Generate proper UUID v5 for conversation using the UUID service (REQUIRED)
    const anonymousId = this.getAnonymousId()
    const timestamp = Date.now()
    const conversationId = uuidService.generateConversationId(anonymousId, timestamp)
    this.saveConversationWithUUID(conversationId, messages)
  }

  /**
   * Save conversation with UUID structure for migration support
   * @param {string} conversationId - UUID for the conversation
   * @param {Array} messages - Array of conversation messages
   */
  saveConversationWithUUID(conversationId, messages) {
    try {
      if (!conversationId || !Array.isArray(messages)) {
        console.warn('Invalid conversation data for UUID storage')
        return
      }

      // Convert messages array to object keyed by message UUIDs
      const messagesObject = {}
      const messageOrder = []
      const limitedMessages = messages.slice(-this.maxHistorySize) // Apply size limit

      limitedMessages.forEach((message, index) => {
        // Skip progress messages - they're temporary and shouldn't be stored
        if (message.isProgress) {
          return
        }

        // Check for message ID in both field names (message_id and messageId)
        let messageId = message.message_id || message.messageId

        // Only generate UUID for user messages (assistant messages come with IDs from backend)
        if (!messageId && message.role === 'user') {
          // Ensure timestamp is a valid number
          let timestamp = message.timestamp
          if (!timestamp || isNaN(Number(timestamp))) {
            timestamp = Date.now() + index
          } else {
            timestamp = Number(timestamp)
          }
          messageId = uuidService.generateMessageId(conversationId, timestamp, message.role)
        }

        // If assistant message doesn't have ID (shouldn't happen), skip it
        if (!messageId) {
          console.warn('Assistant message missing message_id, skipping:', message)
          return
        }

        // Store message with its UUID as key, including error state
        const messageData = {
          message_id: messageId,
          role: message.role,
          timestamp: message.timestamp || Date.now() + index,
          conversation_id: conversationId,
          parts: message.parts || [], // Store parts array for interactive components (source of truth)
        }

        // Preserve error state and type for proper restoration
        if (message.isError) {
          messageData.isError = true
          messageData.type = 'error'
        }
        if (message.type) {
          messageData.type = message.type
        }

        messagesObject[messageId] = messageData

        // Keep track of message order
        messageOrder.push(messageId)
      })

      // Create a fresh conversations object with only the current conversation
      // This prevents accumulation of multiple conversations
      const conversations = {
        [conversationId]: {
          conversation_id: conversationId,
          messages: messagesObject, // Messages as object keyed by UUID
          message_order: messageOrder, // Array to maintain message sequence
          updated_at: Date.now(),
          created_at: Date.now(), // Always use current time for single conversation
          anonymous_id: this.getAnonymousId(),
        },
      }

      // Replace entire localStorage content with single conversation
      localStorage.setItem(this.storageKeys.conversationsUUID, JSON.stringify(conversations))
    } catch (error) {
      console.error('Failed to save conversation with UUID:', error)

      // If storage quota exceeded, clear old conversations and throw error
      if (error.name === 'QuotaExceededError') {
        this.clearOldConversationsUUID()
        throw new Error('Storage quota exceeded. Old conversations cleared. Please try again.')
      }

      throw error
    }
  }

  /**
   * Switch to a specific conversation, clearing any existing conversations.
   * This method ensures only one conversation exists in localStorage at any time.
   * @param {string} conversationId - UUID of the conversation to switch to
   * @param {Array} messages - Array of conversation messages (optional, defaults to empty)
   */
  switchToConversation(conversationId, messages = []) {
    // Clear existing conversations first to prevent accumulation
    this.clearConversations()

    // Save with the database conversation ID
    this.saveConversationWithUUID(conversationId, messages)

    // CRITICAL FIX: Ensure et_conversations_uuid uses database values
    try {
      const conversations = this.getConversationsWithUUID()
      if (conversations[conversationId]) {
        // Update message UUIDs to use database values
        conversations[conversationId].messages = messages.map((msg) => ({
          ...msg,
          conversation_uuid: conversationId, // Force database ID
          message_uuid: msg.message_uuid || msg.messageId || msg.id, // Use database message ID
        }))

        localStorage.setItem(this.storageKeys.conversationsUUID, JSON.stringify(conversations))
      }
    } catch (error) {
      console.warn('[ClientTokenService] Failed to update conversation with database UUIDs:', error)
    }

    return conversationId
  }

  /**
   * Get all UUID-based conversations with backward compatibility migration
   * @returns {Object} Object with conversation IDs as keys
   */
  getConversationsWithUUID() {
    try {
      const data = localStorage.getItem(this.storageKeys.conversationsUUID)
      if (!data) {
        // Check for legacy conversation history format and migrate if needed
        return this.migrateLegacyConversationHistory()
      }

      const conversations = JSON.parse(data)

      // Validate and migrate existing conversations if they lack parts structure
      return this.validateAndMigrateConversations(conversations)
    } catch (error) {
      console.warn('Failed to load UUID conversations:', error)
      // Attempt legacy migration as fallback
      return this.migrateLegacyConversationHistory()
    }
  }

  /**
   * Validates existing conversations and migrates them to the enhanced parts structure if needed.
   * @param {Object} conversations - Conversations object from storage
   * @returns {Object} Validated and migrated conversations
   */
  validateAndMigrateConversations(conversations) {
    if (!conversations || typeof conversations !== 'object') {
      return {}
    }

    const migratedConversations = {}
    let migrationOccurred = false

    Object.entries(conversations).forEach(([conversationId, conversation]) => {
      if (!conversation || !conversation.messages) {
        return
      }

      // Check if migration is needed for this conversation
      const needsMigration = this.conversationNeedsMigration(conversation)

      if (needsMigration) {
        const migratedConversation = this.migrateConversation(conversation)
        migratedConversations[conversationId] = migratedConversation
        migrationOccurred = true
      } else {
        migratedConversations[conversationId] = conversation
      }
    })

    // Save migrated conversations back to storage if changes were made
    if (migrationOccurred) {
      try {
        localStorage.setItem(
          this.storageKeys.conversationsUUID,
          JSON.stringify(migratedConversations)
        )
        // Migration completed successfully
      } catch (error) {
        console.warn('Failed to save migrated conversations:', error)
      }
    }

    return migratedConversations
  }

  /**
   * Checks if a conversation needs migration to the enhanced parts structure.
   * @param {Object} conversation - Conversation object to check
   * @returns {boolean} True if migration is needed
   */
  conversationNeedsMigration(conversation) {
    if (!conversation.messages) return false

    // Check if any messages lack proper parts structure
    const messages = Object.values(conversation.messages)
    return messages.some((message) => {
      // If message has content but no parts array, it needs migration
      if ((message.content || message.message) && !Array.isArray(message.parts)) {
        return true
      }

      // If message has parts but they lack proper structure, it needs migration
      if (Array.isArray(message.parts) && message.parts.length > 0) {
        return message.parts.some(
          (part) =>
            // Check if part lacks essential fields
            typeof part.order !== 'number' ||
            !part.timestamp ||
            typeof part.isComponent !== 'boolean'
        )
      }

      return false
    })
  }

  /**
   * Migrates a conversation to the enhanced parts structure.
   * @param {Object} conversation - Conversation to migrate
   * @returns {Object} Migrated conversation
   */
  migrateConversation(conversation) {
    const migratedConversation = { ...conversation }

    if (conversation.messages) {
      const migratedMessages = {}

      Object.entries(conversation.messages).forEach(([messageId, message]) => {
        migratedMessages[messageId] = this.migrateMessage(message)
      })

      migratedConversation.messages = migratedMessages
    }

    return migratedConversation
  }

  /**
   * Migrates a message to the enhanced parts structure.
   * @param {Object} message - Message to migrate
   * @returns {Object} Migrated message
   */
  migrateMessage(message) {
    const migratedMessage = { ...message }

    // If message has content but no parts, create parts from content
    if ((message.content || message.message) && !Array.isArray(message.parts)) {
      const content = message.content || message.message

      migratedMessage.parts = [
        {
          type: message.type || 'response',
          message: content,
          timestamp: message.timestamp || new Date().toISOString(),
          order: 0,
          isComponent: false,
          componentType: null,
          metadata: {},
        },
      ]
    }
    // If message has parts but they need structure enhancement
    else if (Array.isArray(message.parts) && message.parts.length > 0) {
      migratedMessage.parts = message.parts.map((part, index) => ({
        type: part.type || 'response',
        message: part.message || part.content || '',
        timestamp: part.timestamp || new Date().toISOString(),
        order: typeof part.order === 'number' ? part.order : index,
        isComponent: Boolean(part.isComponent),
        componentType: part.componentType || null,
        metadata: part.metadata || {},

        // Preserve existing fields
        ...part,
      }))
    }
    // If message has no content at all, create empty parts array
    else {
      migratedMessage.parts = []
    }

    return migratedMessage
  }

  /**
   * Migrates legacy conversation history formats to the new UUID-based structure.
   * @returns {Object} Migrated conversations or empty object
   */
  migrateLegacyConversationHistory() {
    try {
      // Check for old 'conversationHistory' storage format
      const legacyHistory = localStorage.getItem('conversationHistory')
      if (legacyHistory) {
        const messages = JSON.parse(legacyHistory)
        if (Array.isArray(messages) && messages.length > 0) {
          // Migrating legacy conversation history to UUID structure

          // Create a new conversation ID for the legacy messages
          const anonymousId = this.getAnonymousId()
          const timestamp = Date.now()
          const conversationId = uuidService.generateConversationId(anonymousId, timestamp)

          // Migrate messages to new format
          const migratedMessages = messages.map((message) => this.migrateMessage(message))

          // Save in new format
          this.saveConversationWithUUID(conversationId, migratedMessages)

          // Remove legacy storage
          localStorage.removeItem('conversationHistory')

          // Return the new conversation structure
          return this.getConversationsWithUUID()
        }
      }

      return {}
    } catch (error) {
      console.warn('Failed to migrate legacy conversation history:', error)
      return {}
    }
  }

  /**
   * Get specific conversation by UUID
   * @param {string} conversationId - UUID of the conversation
   * @returns {Object|null} Conversation data or null if not found
   */
  getConversationByUUID(conversationId) {
    const conversations = this.getConversationsWithUUID()
    return conversations[conversationId] || null
  }

  /**
   * Clear old UUID conversations to free storage space
   */
  clearOldConversationsUUID() {
    try {
      const conversations = this.getConversationsWithUUID()
      const conversationEntries = Object.entries(conversations)

      // Sort by updated_at timestamp
      conversationEntries.sort((a, b) => (b[1].updated_at || 0) - (a[1].updated_at || 0))

      // Keep only the most recent conversations
      const maxConversations = 5
      const keptConversations = {}

      conversationEntries.slice(0, maxConversations).forEach(([id, data]) => {
        keptConversations[id] = data
      })

      localStorage.setItem(this.storageKeys.conversationsUUID, JSON.stringify(keptConversations))
    } catch (error) {
      console.warn('Failed to clear old UUID conversations:', error)
    }
  }

  /**
   * Get session analytics data
   * @returns {Object} Session data for analytics
   */
  getSessionData() {
    const token = this.getCurrentToken()
    const payload = this.parseToken(token)
    const sessionStartTime = this.getOrCreateSessionStartTime()

    return {
      anonymousId: this.getAnonymousId(),
      sessionId: payload?.sessionId,
      sessionStartTime,
      sessionDuration: Date.now() - sessionStartTime,
      conversationCount: this.getConversationHistory().length,
      tokenExpiry: payload?.exp ? payload.exp * 1000 : null,
    }
  }

  /**
   * Track user linking for analytics
   * @param {string} anonymousId - Anonymous ID being linked
   * @param {string} userId - User ID being linked to
   * @param {Object} metadata - Additional tracking metadata
   */
  trackUserLink(anonymousId, userId, metadata = {}) {
    const linkingData = {
      event: 'user_link',
      anonymousId,
      userId,
      timestamp: Date.now(),
      sessionData: this.getSessionData(),
      ...metadata,
    }

    // Store linking event for analytics pickup
    try {
      const existingLinks = JSON.parse(sessionStorage.getItem('et_user_links') || '[]')
      existingLinks.push(linkingData)

      sessionStorage.setItem('et_user_links', JSON.stringify(existingLinks))
    } catch (error) {
      console.warn('Failed to track user link:', error)
    }

    return linkingData
  }

  /**
   * Clear conversation history only (preserves session, anonymous ID, and UUID keys for restoration)
   * Used during conversation switches to prevent accumulation while preserving restoration metadata
   */
  clearConversations() {
    try {
      // Clear conversation history from localStorage
      localStorage.removeItem(this.storageKeys.conversationsUUID)

      // NOTE: Preserve UUID keys for conversation restoration
      // et_ai_conversation_id and et_ai_conversation_start_time should persist
      // during conversation switches to enable consistent restoration behavior

      return true
    } catch (error) {
      console.warn('Failed to clear conversations:', error)
      return false
    }
  }

  /**
   * Clear ALL conversation data including UUID keys (for complete cleanup scenarios)
   * Used for logout, sign out, or complete reset where restoration should not happen
   */
  clearAllConversationData() {
    try {
      // Clear conversation history from localStorage
      localStorage.removeItem(this.storageKeys.conversationsUUID)

      // Clear conversation-related localStorage items (UUID keys)
      localStorage.removeItem('et_ai_conversation_id')
      localStorage.removeItem('et_ai_conversation_start_time')

      return true
    } catch (error) {
      console.warn('Failed to clear all conversation data:', error)
      return false
    }
  }

  /**
   * Clear all session data (for sign out or reset)
   * @param {boolean} preserveAnonymousId - Whether to keep anonymous ID for tracking
   */
  clearSession(preserveAnonymousId = true) {
    try {
      // Clear session storage completely
      sessionStorage.removeItem(this.storageKeys.sessionToken)
      sessionStorage.removeItem(this.storageKeys.sessionStartTime)
      sessionStorage.removeItem('et_user_links')

      // Clear ALL conversation data including UUID keys (complete cleanup for logout)
      this.clearAllConversationData()

      // Clear Turnstile verification
      localStorage.removeItem('turnstile_verified')

      // Optionally clear anonymous ID (for complete reset)
      if (!preserveAnonymousId) {
        localStorage.removeItem(this.storageKeys.anonymousId)
      }

      return true
    } catch (error) {
      console.warn('Failed to clear session:', error)
      return false
    }
  }

  /**
   * Refresh token if expired
   * @returns {string} New or existing valid token
   */
  refreshTokenIfNeeded() {
    const currentToken = sessionStorage.getItem(this.storageKeys.sessionToken)

    if (!currentToken || !this.isTokenValid(currentToken)) {
      return this.generateToken()
    }

    return currentToken
  }
}

// Export singleton instance
const clientTokenService = new ClientTokenService()
export default clientTokenService
